/**
 * Frontend symbol validation utility that mirrors the backend SymbolConverter.
 * 
 * This provides client-side validation for trading pair symbols to give users
 * immediate feedback without needing to contact the backend.
 */

export interface SymbolValidationResult {
  isValid: boolean;
  error?: string;
  suggestion?: string;
  symbol?: string;
  baseAsset?: string;
  quoteAsset?: string;
}

export class SymbolValidator {
  private static readonly QUOTE_CURRENCIES = [
    'USDT', 'USDC', 'BUSD', 'DAI', 'TUSD', 'USDP',  // Stablecoins
    'BTC', 'ETH', 'BNB', 'XRP', 'ADA', 'DOT', 'LINK',  // Major cryptos
    'EUR', 'USD', 'GBP', 'JPY', 'AUD', 'CAD',  // Fiat currencies
    'TRX', 'LTC', 'BCH', 'EOS', 'XLM', 'ATOM',  // Other cryptos
    'FDUSD', 'VAI', 'NGN', 'RUB', 'TRY', 'ZAR'  // Regional/other
  ];

  private static readonly QUOTE_SET = new Set(SymbolValidator.QUOTE_CURRENCIES);

  // Regex patterns for validation
  private static readonly API_PATTERN = /^[A-Z0-9]{3,20}$/;
  private static readonly POSITION_PATTERN = /^[A-Z0-9]{1,15}\/[A-Z0-9]{2,8}$/;

  /**
   * Validate and normalize a trading pair symbol.
   */
  static validateSymbol(input: string): SymbolValidationResult {
    try {
      // Basic input validation
      if (!input || typeof input !== 'string') {
        return {
          isValid: false,
          error: 'Trading pair symbol is required'
        };
      }

      // Clean and normalize input
      const cleanSymbol = input.trim().toUpperCase();
      
      if (!cleanSymbol) {
        return {
          isValid: false,
          error: 'Trading pair symbol cannot be empty'
        };
      }

      // Check for invalid characters
      if (!/^[A-Z0-9/]+$/.test(cleanSymbol)) {
        return {
          isValid: false,
          error: 'Trading pair can only contain letters, numbers, and forward slash (/)',
          suggestion: 'Example: BTC/USDT or BTCUSDT'
        };
      }

      // Determine format and validate
      if (cleanSymbol.includes('/')) {
        return this.validatePositionFormat(cleanSymbol);
      } else {
        return this.validateApiFormat(cleanSymbol);
      }

    } catch (error) {
      return {
        isValid: false,
        error: 'Invalid trading pair format',
        suggestion: 'Please use format like BTC/USDT or BTCUSDT'
      };
    }
  }

  /**
   * Validate position format (BTC/USDT).
   */
  private static validatePositionFormat(symbol: string): SymbolValidationResult {
    if (!this.POSITION_PATTERN.test(symbol)) {
      return {
        isValid: false,
        error: 'Invalid trading pair format',
        suggestion: 'Use format like BTC/USDT (base currency/quote currency)'
      };
    }

    const parts = symbol.split('/');
    if (parts.length !== 2) {
      return {
        isValid: false,
        error: 'Trading pair must have exactly one forward slash (/)',
        suggestion: 'Example: BTC/USDT'
      };
    }

    const [baseAsset, quoteAsset] = parts;

    if (!baseAsset || !quoteAsset) {
      return {
        isValid: false,
        error: 'Both base and quote currencies are required',
        suggestion: 'Example: BTC/USDT (Bitcoin priced in USDT)'
      };
    }

    // Validate quote currency
    if (!this.QUOTE_SET.has(quoteAsset)) {
      const suggestions = this.findSimilarQuoteCurrencies(quoteAsset);
      return {
        isValid: false,
        error: `Unsupported quote currency: ${quoteAsset}`,
        suggestion: suggestions.length > 0 
          ? `Did you mean: ${suggestions.join(', ')}?`
          : 'Supported quote currencies include: USDT, USDC, BTC, ETH, EUR, USD, etc.'
      };
    }

    return {
      isValid: true,
      symbol: symbol,
      baseAsset,
      quoteAsset
    };
  }

  /**
   * Validate API format (BTCUSDT).
   */
  private static validateApiFormat(symbol: string): SymbolValidationResult {
    if (!this.API_PATTERN.test(symbol)) {
      return {
        isValid: false,
        error: 'Invalid trading pair format',
        suggestion: 'Use format like BTCUSDT or BTC/USDT'
      };
    }

    // Try to detect quote currency
    const quoteDetection = this.detectQuoteCurrency(symbol);
    if (!quoteDetection.success) {
      return {
        isValid: false,
        error: quoteDetection.error || 'Cannot identify quote currency',
        suggestion: 'Make sure the symbol ends with a supported quote currency like USDT, BTC, ETH, etc.'
      };
    }

    const { baseAsset, quoteAsset } = quoteDetection;
    const positionFormat = `${baseAsset}/${quoteAsset}`;

    return {
      isValid: true,
      symbol: positionFormat,
      baseAsset,
      quoteAsset
    };
  }

  /**
   * Detect quote currency from API format symbol.
   */
  private static detectQuoteCurrency(symbol: string): {
    success: boolean;
    baseAsset?: string;
    quoteAsset?: string;
    error?: string;
  } {
    // Try each known quote currency (longest first to avoid partial matches)
    const sortedQuotes = [...this.QUOTE_CURRENCIES].sort((a, b) => b.length - a.length);
    
    for (const quote of sortedQuotes) {
      if (symbol.endsWith(quote) && symbol.length > quote.length) {
        const base = symbol.slice(0, -quote.length);
        if (base.length >= 1) {
          return {
            success: true,
            baseAsset: base,
            quoteAsset: quote
          };
        }
      }
    }

    // Fallback: try common patterns
    if (symbol.length >= 6) {
      // Try 4-character quote (most common)
      const potentialQuote4 = symbol.slice(-4);
      if (['USDT', 'USDC', 'BUSD'].includes(potentialQuote4)) {
        return {
          success: true,
          baseAsset: symbol.slice(0, -4),
          quoteAsset: potentialQuote4
        };
      }
      
      // Try 3-character quote
      const potentialQuote3 = symbol.slice(-3);
      if (['BTC', 'ETH', 'BNB', 'USD', 'EUR'].includes(potentialQuote3)) {
        return {
          success: true,
          baseAsset: symbol.slice(0, -3),
          quoteAsset: potentialQuote3
        };
      }
    }

    return {
      success: false,
      error: `Cannot identify quote currency in ${symbol}`
    };
  }

  /**
   * Find similar quote currencies for suggestions.
   */
  private static findSimilarQuoteCurrencies(input: string): string[] {
    const suggestions: string[] = [];
    const inputLower = input.toLowerCase();

    // Exact length matches
    const sameLength = this.QUOTE_CURRENCIES.filter(q => q.length === input.length);
    for (const quote of sameLength) {
      if (this.getLevenshteinDistance(inputLower, quote.toLowerCase()) <= 1) {
        suggestions.push(quote);
      }
    }

    // If no close matches, suggest popular ones
    if (suggestions.length === 0) {
      if (input.length === 4) {
        suggestions.push('USDT', 'USDC', 'BUSD');
      } else if (input.length === 3) {
        suggestions.push('BTC', 'ETH', 'BNB', 'USD', 'EUR');
      } else {
        suggestions.push('USDT', 'BTC', 'ETH');
      }
    }

    return suggestions.slice(0, 3); // Limit to 3 suggestions
  }

  /**
   * Calculate Levenshtein distance for similarity matching.
   */
  private static getLevenshteinDistance(str1: string, str2: string): number {
    const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));

    for (let i = 0; i <= str1.length; i++) matrix[0][i] = i;
    for (let j = 0; j <= str2.length; j++) matrix[j][0] = j;

    for (let j = 1; j <= str2.length; j++) {
      for (let i = 1; i <= str1.length; i++) {
        if (str1[i - 1] === str2[j - 1]) {
          matrix[j][i] = matrix[j - 1][i - 1];
        } else {
          matrix[j][i] = Math.min(
            matrix[j - 1][i - 1] + 1, // substitution
            matrix[j][i - 1] + 1,     // insertion
            matrix[j - 1][i] + 1      // deletion
          );
        }
      }
    }

    return matrix[str2.length][str1.length];
  }

  /**
   * Convert between symbol formats.
   */
  static convertToPositionFormat(symbol: string): string {
    const validation = this.validateSymbol(symbol);
    if (validation.isValid && validation.symbol) {
      return validation.symbol;
    }
    throw new Error(validation.error || 'Invalid symbol format');
  }

  /**
   * Convert to API format (remove slash).
   */
  static convertToApiFormat(symbol: string): string {
    const validation = this.validateSymbol(symbol);
    if (validation.isValid && validation.baseAsset && validation.quoteAsset) {
      return `${validation.baseAsset}${validation.quoteAsset}`;
    }
    throw new Error(validation.error || 'Invalid symbol format');
  }

  /**
   * Get list of supported quote currencies.
   */
  static getSupportedQuoteCurrencies(): string[] {
    return [...this.QUOTE_CURRENCIES];
  }

  /**
   * Check if a quote currency is supported.
   */
  static isQuoteCurrencySupported(quote: string): boolean {
    return this.QUOTE_SET.has(quote.toUpperCase());
  }
}

/**
 * Convenience function for quick validation.
 */
export function validateTradingPair(symbol: string): SymbolValidationResult {
  return SymbolValidator.validateSymbol(symbol);
}

/**
 * Convenience function to check if symbol is valid.
 */
export function isValidTradingPair(symbol: string): boolean {
  return SymbolValidator.validateSymbol(symbol).isValid;
}

export default SymbolValidator;